import { LinearFilter, SRGBColorSpace, Texture } from 'three';

import { OrthoTileStore } from '../../../stores/geodata-store-factory/ortho-tile-store/ortho-tile-store';
import { TileCoord } from './ortho-tile-typings';

/**
 * TileLoader: fetch/decode
 */
export class TileLoader {
  private readonly store: OrthoTileStore;

  constructor(params: { store: OrthoTileStore }) {
    this.store = params.store;
  }

  async loadTileTexture(coord: TileCoord, signal?: AbortSignal): Promise<Texture> {
    if (signal?.aborted) {
      throw signal.reason ?? new DOMException('Aborted', 'AbortError');
    }

    const url = await this.store.loadTileUrl(coord.z, coord.x, coord.y);

    if (signal?.aborted) {
      throw signal.reason ?? new DOMException('Aborted', 'AbortError');
    }

    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`Tile fetch failed: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();

    if (signal?.aborted) {
      throw signal.reason ?? new DOMException('Aborted', 'AbortError');
    }

    // Decode off the main thread. imageOrientation:'flipY' pre-flips to WebGL
    // convention so we can disable Three.js's UNPACK_FLIP_Y_WEBGL path, which
    // is unreliable for ImageBitmap across browsers.
    const bitmap = await createImageBitmap(blob, { imageOrientation: 'flipY' });

    if (signal?.aborted) {
      bitmap.close();
      throw signal.reason ?? new DOMException('Aborted', 'AbortError');
    }

    const texture = new Texture(bitmap);
    texture.colorSpace = SRGBColorSpace;
    texture.flipY = false;
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;

    return texture;
  }
}
